import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ScanLine } from 'lucide-react';
import jsQR from 'jsqr';
import { tryUnprotectParam, screenService } from '@kss/api';
import { setTable } from '@kss/ui';

/** Pull the table token out of a scanned/typed value — a bare code ("sg-07"), an encrypted
 * token ("enc.…"), a "/t/<token>" path, a "?t=<token>" home URL, or a full URL of either. */
function parseTableToken(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  const path = s.match(/\/t\/([^/?#]+)/i);
  if (path) return decodeURIComponent(path[1]!);
  const q = s.match(/[?&]t=([^&#]+)/i);
  if (q) return decodeURIComponent(q[1]!);
  const bare = s.match(/^(enc\.[A-Za-z0-9_-]+|[a-z]{2,4}-?\d{1,3})$/i);
  if (bare) return bare[1]!;
  return null;
}

/** Scan a table QR (camera where supported) or type the table code, then open that table. */
export function QrScanModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [manual, setManual] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  // Resolve a scanned/typed value → a table, verifying it exists AND is enabled before ordering.
  async function openTable(raw: string, cleanup?: () => void) {
    const s = raw.trim();
    // A QR for a different restaurant (subdomain) → hand off to that site.
    try {
      const u = new URL(s);
      if (u.hostname && u.hostname !== window.location.hostname) {
        cleanup?.();
        onClose();
        window.location.assign(s);
        return;
      }
    } catch {
      /* not a full URL — fall through */
    }
    const token = parseTableToken(s);
    if (!token) {
      setNote('That doesn’t look like a table QR.');
      return;
    }
    setBusy(true);
    // A token this build can't read is a bad scan, not a table code — say so rather than
    // storing "enc.…" as the customer's table and failing every call from then on.
    const decoded = await tryUnprotectParam(token);
    if (decoded === null) {
      setBusy(false);
      setNote('Couldn’t read that table QR — try scanning again, or type your table code.');
      return;
    }
    const table = decoded.toLowerCase();
    // Gate on the live table status — a disabled / non-existent table can't take orders.
    try {
      const menu = await screenService.getQrMenu(table);
      if (!menu.tableAvailable) {
        setBusy(false);
        setNote('This table isn’t available right now — please ask our staff.');
        return;
      }
    } catch {
      setBusy(false);
      setNote('Couldn’t verify that table — check your connection and try again.');
      return;
    }
    if (!setTable(table)) {
      setBusy(false);
      setNote('That doesn’t look like a table code — please ask our staff.');
      return;
    }
    cleanup?.();
    setBusy(false);
    onClose();
    navigate('/order'); // scan → land straight on the table's menu, ready to order
  }

  useEffect(() => {
    if (!open) return;
    let stream: MediaStream | null = null;
    let raf = 0;
    let cancelled = false;

    const cleanup = () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      stream?.getTracks().forEach((tr) => tr.stop());
    };

    if (!navigator.mediaDevices?.getUserMedia) {
      setNote('Camera isn’t available here — enter your table code below.');
      return cleanup;
    }

    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (cancelled) {
          stream.getTracks().forEach((tr) => tr.stop());
          return;
        }
        const v = videoRef.current;
        if (!v) return;
        v.srcObject = stream;
        await v.play().catch(() => {});

        // Prefer the native BarcodeDetector where present; otherwise decode every frame with jsQR
        // (works in Firefox, Safari, iOS, older Chrome — the previous code just gave up on those).
        const Det = (window as unknown as { BarcodeDetector?: any }).BarcodeDetector;
        const detector = Det ? new Det({ formats: ['qr_code'] }) : null;
        const canvas = canvasRef.current ?? document.createElement('canvas');
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        let handled = false;

        const tick = async () => {
          if (cancelled || handled) return;
          const vid = videoRef.current;
          if (vid && vid.readyState >= 2 && vid.videoWidth > 0) {
            try {
              if (detector) {
                const codes = await detector.detect(vid);
                if (codes && codes.length) {
                  handled = true;
                  return openTable(codes[0].rawValue as string, cleanup);
                }
              } else if (ctx) {
                canvas.width = vid.videoWidth;
                canvas.height = vid.videoHeight;
                ctx.drawImage(vid, 0, 0, canvas.width, canvas.height);
                const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const code = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
                if (code && code.data) {
                  handled = true;
                  return openTable(code.data, cleanup);
                }
              }
            } catch {
              /* frame not ready yet */
            }
          }
          raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      } catch (err) {
        const name = (err as { name?: string })?.name;
        setNote(
          name === 'NotAllowedError'
            ? 'Camera permission was blocked — allow it in your browser, or enter your table code below.'
            : 'Couldn’t open the camera — enter your table code below.'
        );
      }
    })();

    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div className='fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4' onClick={onClose}>
      <div
        className='w-full max-w-[360px] overflow-hidden rounded-2xl bg-surface-card shadow-pop'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='flex items-center justify-between border-b border-border px-4 py-3'>
          <span className='flex items-center gap-2 font-display text-sm font-bold'>
            <ScanLine size={16} className='text-primary' /> Scan table QR
          </span>
          <button onClick={onClose} aria-label='Close' className='text-content-secondary hover:text-content-primary'>
            <X size={18} />
          </button>
        </div>

        <div className='relative aspect-square w-full bg-black'>
          <video ref={videoRef} muted playsInline autoPlay className='h-full w-full object-cover' />
          <canvas ref={canvasRef} className='hidden' />
          <div className='pointer-events-none absolute inset-8 rounded-xl border-2 border-white/80' />
          {busy && (
            <div className='absolute inset-0 flex items-center justify-center bg-black/50 text-[13px] font-semibold text-white'>
              Checking table…
            </div>
          )}
        </div>

        <div className='p-4'>
          {note && <p className='mb-2 text-[11.5px] text-content-muted'>{note}</p>}
          <div className='text-[11px] font-semibold uppercase tracking-wide text-content-muted'>Or enter table code</div>
          <form
            className='mt-1.5 flex gap-2'
            onSubmit={(e) => {
              e.preventDefault();
              if (!busy) openTable(manual);
            }}
          >
            <input
              value={manual}
              // Filtered to what `parseTableToken` actually accepts — a bare code, an "enc.…"
              // token, or a full URL. Dots, colons and query characters must survive (an
              // encrypted token IS "enc.<base64>"), so this only strips whitespace and the
              // angle brackets/quotes that no form of the value ever contains.
              onChange={(e) => setManual(e.target.value.replace(/[\s<>"'`]/g, '').slice(0, 300))}
              maxLength={300}
              aria-label='Table code'
              placeholder='e.g. sg-07'
              className='min-w-0 flex-1 rounded-lg border-[1.5px] border-border bg-surface-card px-3 py-2 text-sm focus:border-primary focus:outline-none'
            />
            <button
              type='submit'
              disabled={busy || manual.trim().length === 0}
              className='rounded-lg bg-primary px-4 py-2 font-display text-xs font-bold text-primary-foreground disabled:opacity-60'
            >
              Open
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
